import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { Image as KonvaImage, Layer, Rect, Stage, Transformer } from 'react-konva';
import {
  AnnotationDocument,
  AnnotationShape,
  ArrowShape,
  BoxShape,
  CropRect,
  SIZE_PRESETS,
  SizePreset,
  ToolId,
  isBoxShape,
  isEffectShape,
  newShapeId,
} from './types';
import { ArrowHandles, ShapeNode, ShapePatch } from './shapes';

export interface AnnotationStageProps {
  image: HTMLImageElement;
  doc: AnnotationDocument;
  onCommit: (next: AnnotationDocument | ((prev: AnnotationDocument) => AnnotationDocument)) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  tool: ToolId;
  color: string;
  size: SizePreset;
  /** Anzeige-Skalierung (1 = Originalpixel). */
  scale: number;
  cropMode: boolean;
  draftCrop: CropRect;
  onDraftCropChange: (crop: CropRect) => void;
  stageRef: React.MutableRefObject<Konva.Stage | null>;
  uiLayerRef: React.MutableRefObject<Konva.Layer | null>;
  /** Neuer Text erstellt bzw. Text doppelt geklickt → Textfeld fokussieren. */
  onTextEdit?: (id: string) => void;
}

const MIN_DRAW_SIZE = 4;
const CROP_DIM = 'rgba(0,0,0,0.55)';

const isDrawTool = (tool: ToolId): boolean =>
  tool === 'rect' ||
  tool === 'ellipse' ||
  tool === 'highlight' ||
  tool === 'pixelate' ||
  tool === 'redact' ||
  tool === 'spotlight' ||
  tool === 'arrow';

const normalizeBox = (x1: number, y1: number, x2: number, y2: number) => ({
  x: Math.min(x1, x2),
  y: Math.min(y1, y2),
  width: Math.abs(x2 - x1),
  height: Math.abs(y2 - y1),
});

const AnnotationStage: React.FC<AnnotationStageProps> = ({
  image,
  doc,
  onCommit,
  selectedId,
  onSelect,
  tool,
  color,
  size,
  scale,
  cropMode,
  draftCrop,
  onDraftCropChange,
  stageRef,
  uiLayerRef,
  onTextEdit,
}) => {
  const imageBounds = useMemo(
    () => ({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height }),
    [image],
  );
  const pixelSize = Math.max(8, Math.round(imageBounds.width / 110));

  const visible: CropRect = useMemo(() => {
    if (cropMode || !doc.crop) return { x: 0, y: 0, ...imageBounds };
    return doc.crop;
  }, [cropMode, doc.crop, imageBounds]);

  const nodeRefs = useRef<Record<string, Konva.Node | null>>({});
  const transformerRef = useRef<Konva.Transformer | null>(null);
  const cropTransformerRef = useRef<Konva.Transformer | null>(null);
  const cropRectRef = useRef<Konva.Rect | null>(null);
  const drawStart = useRef<{ x: number; y: number } | null>(null);
  // Draft synchron im Ref halten: bei sehr schnellen Drags (mousedown/move/up im selben Frame)
  // wuerde der React-State in den Handlern noch den alten Wert liefern.
  const draftRef = useRef<AnnotationShape | null>(null);
  const [draft, setDraftState] = useState<AnnotationShape | null>(null);
  const setDraft = (next: AnnotationShape | null) => {
    draftRef.current = next;
    setDraftState(next);
  };

  const registerNode = useCallback((id: string, node: Konva.Node | null) => {
    nodeRefs.current[id] = node;
  }, []);

  const selectedShape = useMemo(
    () => doc.shapes.find((s) => s.id === selectedId) ?? null,
    [doc.shapes, selectedId],
  );

  /* Transformer an ausgewaehlten Knoten binden (react-konva: imperativ). */
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    const node = selectedId && !cropMode && selectedShape && selectedShape.type !== 'arrow'
      ? nodeRefs.current[selectedId]
      : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedId, selectedShape, cropMode, doc.shapes]);

  useEffect(() => {
    const tr = cropTransformerRef.current;
    if (!tr) return;
    tr.nodes(cropMode && cropRectRef.current ? [cropRectRef.current] : []);
    tr.getLayer()?.batchDraw();
  }, [cropMode, draftCrop]);

  // Funktionale Updates: Konva-Handler koennen zwischen zwei Renders noch mit einem aelteren
  // `doc` gebunden sein; `prev` garantiert, dass keine zuvor angelegte Form verloren geht.
  const updateShape = useCallback(
    (id: string, patch: ShapePatch) => {
      onCommit((prev) => ({
        ...prev,
        shapes: prev.shapes.map((s) => (s.id === id ? ({ ...s, ...patch } as AnnotationShape) : s)),
      }));
    },
    [onCommit],
  );

  const addShape = useCallback(
    (shape: AnnotationShape) => {
      onCommit((prev) => ({ ...prev, shapes: [...prev.shapes, shape] }));
      onSelect(shape.id);
    },
    [onCommit, onSelect],
  );

  const getImagePoint = (stage: Konva.Stage): { x: number; y: number } | null => {
    const p = stage.getPointerPosition();
    if (!p) return null;
    return { x: (p.x - stage.x()) / scale, y: (p.y - stage.y()) / scale };
  };

  const presets = SIZE_PRESETS[size];

  const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (cropMode) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const clickedEmpty = e.target === stage;
    if (!clickedEmpty) return;
    const p = getImagePoint(stage);
    if (!p) return;

    if (tool === 'select') {
      onSelect(null);
      return;
    }
    if (tool === 'text') {
      const id = newShapeId();
      addShape({ id, type: 'text', x: p.x, y: p.y, text: 'Text', fontSize: presets.fontSize, color, strokeWidth: presets.strokeWidth });
      onTextEdit?.(id);
      return;
    }
    if (tool === 'marker') {
      const id = newShapeId();
      onCommit((prev) => {
        const nextNumber = prev.shapes.reduce((max, s) => (s.type === 'marker' ? Math.max(max, s.number) : max), 0) + 1;
        const marker: AnnotationShape = { id, type: 'marker', x: p.x, y: p.y, number: nextNumber, radius: presets.radius, color, strokeWidth: presets.strokeWidth };
        return { ...prev, shapes: [...prev.shapes, marker] };
      });
      onSelect(id);
      return;
    }
    if (isDrawTool(tool)) {
      onSelect(null);
      drawStart.current = p;
      if (tool === 'arrow') {
        setDraft({ id: newShapeId(), type: 'arrow', points: [p.x, p.y, p.x, p.y], color, strokeWidth: presets.strokeWidth });
      } else {
        setDraft({ id: newShapeId(), type: tool, x: p.x, y: p.y, width: 0, height: 0, color, strokeWidth: presets.strokeWidth } as BoxShape);
      }
    }
  };

  const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
    const current = draftRef.current;
    if (!current || !drawStart.current) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const p = getImagePoint(stage);
    if (!p) return;
    const start = drawStart.current;
    if (current.type === 'arrow') {
      setDraft({ ...current, points: [start.x, start.y, p.x, p.y] });
    } else if (isBoxShape(current)) {
      setDraft({ ...current, ...normalizeBox(start.x, start.y, p.x, p.y) });
    }
  };

  const handleMouseUp = (e: KonvaEventObject<MouseEvent>) => {
    let current = draftRef.current;
    if (!current) return;
    const start = drawStart.current;
    // Letzte Mausposition uebernehmen, falls kein mousemove mehr verarbeitet wurde
    const stage = e.target.getStage();
    const p = stage ? getImagePoint(stage) : null;
    if (start && p) {
      if (current.type === 'arrow') current = { ...current, points: [start.x, start.y, p.x, p.y] };
      else if (isBoxShape(current)) current = { ...current, ...normalizeBox(start.x, start.y, p.x, p.y) };
    }
    drawStart.current = null;
    setDraft(null);
    if (!start) return;
    if (current.type === 'arrow') {
      const [x1, y1, x2, y2] = current.points;
      if (Math.hypot(x2 - x1, y2 - y1) >= MIN_DRAW_SIZE) addShape(current);
      return;
    }
    if (isBoxShape(current) && current.width >= MIN_DRAW_SIZE && current.height >= MIN_DRAW_SIZE) {
      addShape(current);
    }
  };

  /* ── Zuschneiden ──────────────────────────────────────────── */

  const clampCrop = (c: CropRect): CropRect => {
    const width = Math.max(16, Math.min(Math.round(c.width), imageBounds.width));
    const height = Math.max(16, Math.min(Math.round(c.height), imageBounds.height));
    const x = Math.max(0, Math.min(Math.round(c.x), imageBounds.width - width));
    const y = Math.max(0, Math.min(Math.round(c.y), imageBounds.height - height));
    return { x, y, width, height };
  };

  const cropDragBound = (pos: Konva.Vector2d): Konva.Vector2d => {
    const stage = stageRef.current;
    if (!stage) return pos;
    const localX = (pos.x - stage.x()) / scale;
    const localY = (pos.y - stage.y()) / scale;
    const clampedX = Math.max(0, Math.min(localX, imageBounds.width - draftCrop.width));
    const clampedY = Math.max(0, Math.min(localY, imageBounds.height - draftCrop.height));
    return { x: clampedX * scale + stage.x(), y: clampedY * scale + stage.y() };
  };

  const effectShapes = doc.shapes.filter(isEffectShape);
  const markShapes = doc.shapes.filter((s) => !isEffectShape(s));
  // Nur im Auswahl-Werkzeug reagieren Formen auf Klicks/Drag. Bei aktivem Zeichen-/Platzier-
  // werkzeug gehen Klicks immer an die Stage, damit z. B. ein Marker auf einer Hervorhebung
  // platziert oder ein Rahmen innerhalb eines Spotlights gezogen werden kann.
  const interactive = !cropMode && tool === 'select';

  const cursor = cropMode
    ? 'default'
    : tool === 'select'
      ? 'default'
      : tool === 'text' || tool === 'marker'
        ? 'copy'
        : 'crosshair';

  const shapeProps = {
    image,
    imageBounds,
    pixelSize,
    scale,
    interactive,
    registerNode,
    onSelect: (id: string) => onSelect(id),
    onChange: updateShape,
    onTextEdit,
  };

  return (
    <Stage
      ref={(s) => {
        stageRef.current = s;
      }}
      width={Math.max(1, Math.round(visible.width * scale))}
      height={Math.max(1, Math.round(visible.height * scale))}
      scaleX={scale}
      scaleY={scale}
      x={-visible.x * scale}
      y={-visible.y * scale}
      style={{ cursor, lineHeight: 0 }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Inhalt (wird exportiert) */}
      <Layer>
        <KonvaImage image={image} name="base-image" listening={false} />
        {effectShapes.map((s) => (
          <ShapeNode key={s.id} shape={s} selected={s.id === selectedId} {...shapeProps} />
        ))}
        {markShapes.map((s) => (
          <ShapeNode key={s.id} shape={s} selected={s.id === selectedId} {...shapeProps} />
        ))}
        {draft && (
          <ShapeNode
            key="draft"
            shape={draft}
            selected={false}
            {...shapeProps}
            interactive={false}
          />
        )}
      </Layer>

      {/* UI (nicht exportiert) */}
      <Layer
        ref={(l) => {
          uiLayerRef.current = l;
        }}
      >
        {!cropMode && (
          <Transformer
            ref={transformerRef}
            rotateEnabled={false}
            keepRatio={false}
            ignoreStroke
            anchorSize={11}
            anchorCornerRadius={2}
            anchorStroke="#1e88e5"
            anchorFill="#ffffff"
            borderStroke="#1e88e5"
            borderDash={selectedShape && (selectedShape.type === 'text' || selectedShape.type === 'marker') ? [4, 3] : undefined}
            enabledAnchors={
              selectedShape && (selectedShape.type === 'text' || selectedShape.type === 'marker')
                ? []
                : ['top-left', 'top-center', 'top-right', 'middle-right', 'middle-left', 'bottom-left', 'bottom-center', 'bottom-right']
            }
            boundBoxFunc={(oldBox, newBox) =>
              newBox.width < MIN_DRAW_SIZE * scale || newBox.height < MIN_DRAW_SIZE * scale ? oldBox : newBox
            }
          />
        )}
        {!cropMode && selectedShape && selectedShape.type === 'arrow' && (
          <ArrowHandles
            shape={selectedShape as ArrowShape}
            scale={scale}
            getArrowNode={() => (nodeRefs.current[selectedShape.id] as Konva.Arrow | null) ?? null}
            onChange={updateShape}
          />
        )}

        {cropMode && (
          <>
            <Rect x={0} y={0} width={imageBounds.width} height={Math.max(0, draftCrop.y)} fill={CROP_DIM} listening={false} />
            <Rect
              x={0}
              y={draftCrop.y + draftCrop.height}
              width={imageBounds.width}
              height={Math.max(0, imageBounds.height - draftCrop.y - draftCrop.height)}
              fill={CROP_DIM}
              listening={false}
            />
            <Rect x={0} y={draftCrop.y} width={Math.max(0, draftCrop.x)} height={draftCrop.height} fill={CROP_DIM} listening={false} />
            <Rect
              x={draftCrop.x + draftCrop.width}
              y={draftCrop.y}
              width={Math.max(0, imageBounds.width - draftCrop.x - draftCrop.width)}
              height={draftCrop.height}
              fill={CROP_DIM}
              listening={false}
            />
            <Rect
              ref={cropRectRef}
              x={draftCrop.x}
              y={draftCrop.y}
              width={draftCrop.width}
              height={draftCrop.height}
              fill="rgba(0,0,0,0)"
              stroke="#ffffff"
              strokeWidth={1.5 / scale}
              dash={[8 / scale, 5 / scale]}
              draggable
              dragBoundFunc={cropDragBound}
              onDragEnd={(e) => onDraftCropChange(clampCrop({ ...draftCrop, x: e.target.x(), y: e.target.y() }))}
              onTransformEnd={(e) => {
                const n = e.target;
                const next = clampCrop({
                  x: n.x(),
                  y: n.y(),
                  width: n.width() * n.scaleX(),
                  height: n.height() * n.scaleY(),
                });
                n.scaleX(1);
                n.scaleY(1);
                onDraftCropChange(next);
              }}
            />
            <Transformer
              ref={cropTransformerRef}
              rotateEnabled={false}
              keepRatio={false}
              anchorSize={14}
              anchorCornerRadius={3}
              anchorStroke="#ffffff"
              anchorFill="#1e88e5"
              borderStroke="#ffffff"
              ignoreStroke
              boundBoxFunc={(oldBox, newBox) => {
                // Boxen sind in absoluten Stage-Koordinaten; auf die Bildflaeche begrenzen (clampen statt ablehnen)
                const stage = stageRef.current;
                if (!stage) return newBox;
                const minX = stage.x();
                const minY = stage.y();
                const maxX = minX + imageBounds.width * scale;
                const maxY = minY + imageBounds.height * scale;
                const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));
                const x = clamp(newBox.x, minX, maxX);
                const y = clamp(newBox.y, minY, maxY);
                const right = clamp(newBox.x + newBox.width, minX, maxX);
                const bottom = clamp(newBox.y + newBox.height, minY, maxY);
                const width = right - x;
                const height = bottom - y;
                if (width < 16 * scale || height < 16 * scale) return oldBox;
                return { ...newBox, x, y, width, height };
              }}
            />
          </>
        )}
      </Layer>
    </Stage>
  );
};

export default AnnotationStage;
